import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { EnhancedChatMessage } from './EnhancedChatMessage';
import { cn } from '../../lib/utils';

interface ChatMessage {
  id: string;
  message: string;
  reply?: string;
  timestamp: string;
  agent_type?: string;
  confidence?: number;
  suggestedFollowups?: string[];
  sources?: any[];
  isLoading?: boolean;
  isStreaming?: boolean;
}

interface StreamingChatWidgetProps {
  apiEndpoint?: string;
  streamingEndpoint?: string;
  siteId: string;
  sessionId: string;
  visitorId: string;
  pageContent?: string;
  pageUrl?: string;
  className?: string;
  position?: 'bottom-right' | 'bottom-left' | 'embedded';
  theme?: 'light' | 'dark' | 'auto';
  welcomeMessage?: string;
  placeholder?: string;
  useStreaming?: boolean;
}

export function StreamingChatWidget({
  apiEndpoint = '/api/chatbot',
  streamingEndpoint = '/api/chatbot/stream',
  siteId,
  sessionId,
  visitorId,
  pageContent = '',
  pageUrl = window.location.href,
  className,
  position = 'bottom-right',
  theme = 'auto',
  welcomeMessage = "Hi! I'm here to help. Ask me anything about this page!",
  placeholder = "Type your message...",
  useStreaming = true
}: StreamingChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      message: '',
      reply: welcomeMessage,
      timestamp: new Date().toISOString(),
      agent_type: 'assistant'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  
  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);
  
  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);
  
  const sendMessageWithStreaming = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;
    
    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      message: messageText,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    // Add streaming message placeholder
    const streamingMessageId = `streaming-${Date.now()}`;
    const streamingMessage: ChatMessage = {
      id: streamingMessageId,
      message: '',
      reply: '',
      timestamp: new Date().toISOString(),
      isStreaming: true,
      agent_type: 'assistant'
    };
    setMessages(prev => [...prev, streamingMessage]);
    
    try {
      // Close any existing EventSource
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      // Create request body
      const requestBody = JSON.stringify({
        message: messageText,
        siteId,
        sessionId,
        visitorId,
        pageContent,
        pageUrl,
        streamingEnabled: true
      });

      // Initialize EventSource for SSE
      const response = await fetch(streamingEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: requestBody
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                if (data.type === 'connected') {
                  console.log('Connected to streaming endpoint:', data.traceId);
                } else if (data.type === 'chunk') {
                  // Update the streaming message with accumulated content
                  accumulatedContent = data.content;
                  setMessages(prev => prev.map(msg => 
                    msg.id === streamingMessageId 
                      ? { ...msg, reply: accumulatedContent, isStreaming: true }
                      : msg
                  ));
                } else if (data.type === 'complete') {
                  // Finalize the message
                  setMessages(prev => prev.map(msg => 
                    msg.id === streamingMessageId 
                      ? { 
                          ...msg, 
                          reply: data.content, 
                          isStreaming: false,
                          confidence: data.confidence,
                          suggestedFollowups: data.suggestedFollowups,
                          sources: data.sources
                        }
                      : msg
                  ));
                  break;
                } else if (data.type === 'error') {
                  throw new Error(data.error);
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      // Fallback to regular API call
      await sendMessageRegular(messageText);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessageRegular = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;
    
    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      message: messageText,
      timestamp: new Date().toISOString()
    };
    
    if (!messages.find(m => m.id === userMessage.id)) {
      setMessages(prev => [...prev, userMessage]);
    }
    
    const loadingMessage: ChatMessage = {
      id: `loading-${Date.now()}`,
      message: '',
      timestamp: new Date().toISOString(),
      isLoading: true
    };
    setMessages(prev => [...prev, loadingMessage]);
    
    try {
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: messageText,
          siteId,
          sessionId,
          visitorId,
          pageContent,
          url: pageUrl
        })
      });
      
      const data = await response.json();
      
      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.isLoading);
        return [...filtered.slice(0, -1), {
          ...userMessage,
          reply: data.text || data.reply || "I'm sorry, I couldn't process your request.",
          agent_type: data.agent_type || 'assistant',
          confidence: data.confidence,
          suggestedFollowups: data.suggestedFollowups,
          sources: data.sources
        }];
      });
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.isLoading);
        return [...filtered.slice(0, -1), {
          ...userMessage,
          reply: "I'm having trouble connecting right now. Please try again in a moment.",
          agent_type: 'error'
        }];
      });
    }
  };

  const sendMessage = async (messageText: string) => {
    if (useStreaming) {
      await sendMessageWithStreaming(messageText);
    } else {
      await sendMessageRegular(messageText);
    }
    setIsLoading(false);
  };
  
  const handleFollowupClick = (question: string) => {
    sendMessage(question);
  };
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };
  
  const positionClasses = {
    'bottom-right': 'fixed bottom-4 right-4 z-50',
    'bottom-left': 'fixed bottom-4 left-4 z-50',
    'embedded': 'relative w-full h-full'
  };
  
  const widgetClasses = cn(
    positionClasses[position],
    position !== 'embedded' && 'transition-all duration-300',
    className
  );
  
  if (position === 'embedded') {
    return (
      <div className={cn("flex flex-col bg-white dark:bg-gray-900 rounded-lg shadow-xl", widgetClasses)}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">AI Assistant</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {useStreaming ? 'Streaming enabled' : 'Always here to help'}
              </p>
            </div>
          </div>
        </div>
        
        {/* Messages */}
        <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
          <div className="space-y-4">
            {messages.map((msg) => (
              msg.isLoading ? (
                <div key={msg.id} className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              ) : msg.isStreaming ? (
                <div key={msg.id} className="space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                      <Sparkles className="h-3 w-3 text-primary animate-pulse" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm">{msg.reply || '...'}</p>
                      <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-1"></span>
                    </div>
                  </div>
                </div>
              ) : (
                <EnhancedChatMessage
                  key={msg.id}
                  message={msg}
                  onFollowupClick={handleFollowupClick}
                />
              )
            ))}
          </div>
        </ScrollArea>
        
        {/* Input */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              disabled={isLoading}
              className="flex-1"
            />
            <Button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || isLoading}
              size="icon"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Floating widget implementation
  return (
    <div className={widgetClasses}>
      {/* Chat window */}
      {isOpen && (
        <div className="mb-4 w-96 h-[600px] flex flex-col bg-white dark:bg-gray-900 rounded-lg shadow-2xl animate-in slide-in-from-bottom-5">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">AI Assistant</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {useStreaming ? 'Streaming enabled' : 'Always here to help'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
            <div className="space-y-4">
              {messages.map((msg) => (
                msg.isLoading ? (
                  <div key={msg.id} className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Thinking...</span>
                  </div>
                ) : msg.isStreaming ? (
                  <div key={msg.id} className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                        <Sparkles className="h-3 w-3 text-primary animate-pulse" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm">{msg.reply || '...'}</p>
                        <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-1"></span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <EnhancedChatMessage
                    key={msg.id}
                    message={msg}
                    onFollowupClick={handleFollowupClick}
                  />
                )
              ))}
            </div>
          </ScrollArea>
          
          {/* Input */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={placeholder}
                disabled={isLoading}
                className="flex-1"
              />
              <Button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || isLoading}
                size="icon"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Toggle button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        size="icon"
        className="h-14 w-14 rounded-full shadow-lg"
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </Button>
    </div>
  );
}